<script setup lang="ts">
import Typography from '@components/Typography.vue';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { storeToRefs } from 'pinia';
import type { SelectionItem } from '@models/selectionItem.ts';
import { useStreetDistrict } from '@composables/useStreetDistrict.ts';
import { computed, toRef } from 'vue';
import type { SerializedOffer } from '@models/offer.ts';
import type { StationDistance } from '@models/transport.ts';
import SelectionItemTransports from '@organisms/offer/selection-items/components/SelectionItemTransports.vue';
import OfferPrice from '@components/offer-card/OfferPrice.vue';
import { OfficeType } from '@enums/officeType.ts';
import IconCheckCircleFill from '@icons/ph_check-circle-fill.svg';
import IconCircleFill from '@icons/ph_circle-fill.svg';
import CaretRight from '@icons/ph_caret-right.svg';
import Icon from '@components/Icon.vue';
import { getShortReadableDate, isDateInFuture } from '@tools/date';
import { trans, transchoice } from '@elements/translation';
import type { OfferServices } from '@models/offerServices.ts';
import SelectionItemOfferLink from '@organisms/offer/selection-items/components/SelectionItemOfferLink.vue';
import SelectionItemHeaderTag from '@organisms/offer/selection-items/components/SelectionItemHeaderTag.vue';
import { useHover } from '@composables/useHover.ts';
import { useSelectionItemOfferUrl } from '@composables/useSelectionItemOfferUrl.ts';
import SelectionItemMultiOfficeTooltip
  from '@organisms/offer/selection-items/components/SelectionItemMultiOfficeTooltip.vue';

const props = defineProps<{ selectionItem: SelectionItem }>();

const { isMobileOrTablet } = storeToRefs(useScreensizeStore());

const offer = computed<SerializedOffer>(() => props.selectionItem.offer);

const { formattedStreetDistrict } = useStreetDistrict(offer.value.address);

const {
  isHover,
  setHoverInactive,
  setHoverActive,
} = useHover();

const { offerUrl } = useSelectionItemOfferUrl(toRef(() => props.selectionItem));

const stationDistance = computed<StationDistance | null>(() => offer.value.first_station_distance);

const isMultiOffice = computed<boolean>(() => props.selectionItem.offers.length > 1);

const displayedPrice = computed<number>(() => {
  if (!isMultiOffice.value) {
    return props.selectionItem.offer.price;
  }

  return props.selectionItem.total_price;
});

const displayedType = computed<string>(() => {
  if (isMultiOffice.value) {
    return trans('offer_card.display_types.multioffice', { count: props.selectionItem.offers.length });
  }

  return offer.value.office_type?.name ?? offer.value.type_infos;
});

const displayedInfos = computed<string>(() => (!isMultiOffice.value ? offer.value.offer_infos : props.selectionItem.offer_infos));

// @todo refacto in composable
const isSingleOpenspace = computed<boolean>(
  () => !isMultiOffice.value && props.selectionItem.offer.office_type?.code === OfficeType.OPEN_SPACE,
);

const isAvailableSoon = computed<boolean>(() => isDateInFuture(offer.value.availability_date.iso));
const isAvailable = computed<boolean>(() => !isAvailableSoon.value);

const services = computed<OfferServices>(() => props.selectionItem.offer_services);

const offersCapacity = computed<number>(
  () => props.selectionItem.offers.reduce((total, offerItem) => total + offerItem.capacity, 0),
);
</script>

<template>
  <div
    class="selection-item-header"
    :class="{ 'selection-item-header--rented': selectionItem.is_all_offers_rented }"
    @mouseenter="setHoverActive"
    @mouseleave="setHoverInactive"
  >
    <SelectionItemHeaderTag
      v-if="selectionItem.is_removed"
      color="red"
      class="selection-item-header__tag selection-item-header__tag--removed"
    >
      {{ trans('selection_item.status.removed') }}
    </SelectionItemHeaderTag>

    <SelectionItemHeaderTag
      v-else-if="selectionItem.deal"
      class="selection-item-header__tag selection-item-header__tag--contacted"
    >
      <Icon
        :svg="IconCheckCircleFill"
        width="16"
        height="16"
        color="green"
      />

      <span>
        {{ trans('selection_item.status.contacted') }}
      </span>
    </SelectionItemHeaderTag>

    <SelectionItemHeaderTag
      v-else-if="selectionItem.is_all_offers_rented"
      color="white"
      class="selection-item-header__tag selection-item-header__tag--rented"
    >
      {{ trans('selection_item.offer.status.rented') }}
    </SelectionItemHeaderTag>

    <div>
      <div class="selection-item-header__title">
        <Typography variant="content-heading-2">
          {{ formattedStreetDistrict }}
        </Typography>

        <SelectionItemTransports
          v-if="stationDistance"
          :station-distance="stationDistance"
        />

        <SelectionItemOfferLink
          v-show="!selectionItem.is_all_offers_rented && !isMobileOrTablet"
          :url="offerUrl as string"
          :color="isHover ? 'black' : 'gray-darker'"
        >
          <span :class="{ 'selection-item-header__offer-link--hover': isHover }">
            {{ trans('selection_item.offer.see') }}
          </span>
        </SelectionItemOfferLink>
      </div>

      <div class="selection-item-header__price">
        <OfferPrice
          :offer="offer"
          :price="displayedPrice"
          :is-single-openspace="isSingleOpenspace"
          :unit-separator="trans('global.by.word')"
          :color="selectionItem.is_all_offers_rented ? 'gray-dark' : undefined"
        />

        <div class="selection-item-header__availability">
          <Typography
            variant="body-small"
            color="gray-darker"
          >
            • {{ isAvailable
              ? trans('selection_item.offer.status.available')
              : transchoice(
                'selection_item.offer.status.available_soon',
                { date: getShortReadableDate(offer.availability_date.iso) }
              )
            }}
          </Typography>

          <span
            v-if="isAvailable"
            class="selection-item-header__circle"
          >
            <Icon
              :svg="IconCircleFill"
              width="8"
              height="8"
              color="green"
            />
          </span>
        </div>
      </div>
    </div>

    <div class="selection-item-header__description-wrapper">
      <SelectionItemMultiOfficeTooltip
        v-if="isMultiOffice"
        :selection-item="selectionItem"
      />

      <Typography
        variant="body-small"
        color="gray-darker"
        class="selection-item-header__description"
      >
        {{ displayedType }}

        <template v-if="displayedInfos">
          • {{ displayedInfos }}
        </template>

        <template v-if="isMultiOffice">
          • {{ selectionItem.team_size || offersCapacity }}
          {{ transchoice('global.post_office', { count: selectionItem.team_size || offersCapacity }) }}
        </template>

        <template v-if="services.shared_meeting_rooms_count > 0">
          • {{ transchoice('selection_item.service.tag.shared_meeting_rooms', { count: services.shared_meeting_rooms_count }) }}
        </template>

        <template v-if="services.has_private_meeting_room">
          • {{ trans('selection_item.service.tag.private_meeting_room') }}
        </template>

        <template v-if="services.phonebox_count > 0">
          • {{ transchoice('selection_item.service.tag.phonebox_count', { count: services.phonebox_count }) }}
        </template>

        <template v-if="services.has_full_time_access">
          • {{ trans('selection_item.service.tag.full_time_access') }}
        </template>
      </Typography>
    </div>

    <SelectionItemOfferLink
      v-show="!selectionItem.is_all_offers_rented && isMobileOrTablet"
      :url="offerUrl as string"
    >
      {{ trans('selection_item.offer.see') }}
    </SelectionItemOfferLink>
  </div>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.selection-item-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: rem(4);

  &--rented {
    .typography:not(.selection-item-header__tag--rented) {
      color: $colorGrayDark;
    }
  }

  &__tag {
    margin-bottom: rem(8);

    &--removed {
      border-color: $colorRedLight;
    }

    &--contacted {
      border-color: $colorGray;
    }

    &--rented {
      border-color: $colorGrayDark;
      background-color: $colorGrayDark;
    }
  }

  &__title {
    display: flex;
    align-items: center;
    gap: rem(8);
  }

  &__price {
    display: flex;
    align-items: center;
    gap: rem(4);
  }

  &__availability {
    display: flex;
    align-items: center;
    gap: rem(4);
  }

  &__description-wrapper {
    width: 100%;
    display: flex;
    align-items: center;
    gap: rem(4);
  }

  &__description {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__offer-link {
    &--hover {
      font-weight: bold;
    }
  }

  &__office-type {
    display: flex;
    align-items: center;
    gap: rem(4);
  }

  &__circle {
    background-color: $colorGreenLighter;
    width: rem(16);
    height: rem(16);
    border-radius: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}
</style>
